import React, { useState } from "react";
import { FileText, X, Plus, Check } from "lucide-react";
import SuccessModal from "../../../../../shared/components/successModal";
import { CLOUD_UPLOAD } from "../../../../../assets";
import { Checkbox } from "@mui/material";

interface Counsel {
  fullName: string;
  email: string;
  address: string;
  phone: string;
  whatsapp: string;
  firmName: string;
}

const FileAsThirdParty = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [documents, setDocuments] = useState<
    { id: string; file: File | null }[]
  >([{ id: "1", file: null }]);
  const [selectedDefendants, setSelectedDefendants] = useState<string[]>([]);
  const [counsel, setCounsel] = useState<Counsel>({
    fullName: "",
    email: "",
    address: "",
    phone: "",
    whatsapp: "",
    firmName: "",
  });

  const defendants = ["Ademola Jacobs", "Shola Samuels", "Emmanuel Jacobs"];

  const handleDefendantToggle = (defendant: string) => {
    setSelectedDefendants((prev) =>
      prev.includes(defendant)
        ? prev.filter((d) => d !== defendant)
        : [...prev, defendant],
    );
  };

  const updateCounsel = (field: keyof Counsel, value: string) => {
    setCounsel((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string,
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const newDocuments = documents.map((doc) =>
        doc.id === id ? { ...doc, file } : doc,
      );
      setDocuments(newDocuments);
    }
  };

  const addDocumentSlot = () => {
    setDocuments((prev) => [
      ...prev,
      { id: Date.now().toString(), file: null },
    ]);
  };

  const removeDocumentSlot = (index: number) => {
    if (documents.length > 1) {
      setDocuments(documents.filter((_, i) => i !== index));
    } else {
      const newDocs = [...documents];
      newDocs[index].file = null;
      setDocuments(newDocs);
    }
  };

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // Render Steps Indicator
  const renderStepIndicator = () => {
    const steps = [
      { id: 1, label: "Defendants represented" },
      { id: 2, label: "Details of counsel" },
      { id: 3, label: "Upload documents" },
    ];

    return (
      <div className="flex items-center justify-center gap-x-4 mb-12">
        {steps.map((step) => (
          <div key={step.id} className="flex flex-col items-center gap-y-2">
            <div
              className={`w-[97px] h-[12px] rounded-full ${
                currentStep >= step.id ? "bg-[#BDE0CE]" : "bg-[#E9EAEB]"
              }`}
            />
            <span
              className={`text-xs ${
                currentStep >= step.id
                  ? "text-[#1A1A1A] font-medium"
                  : "text-[#9A9A9A]"
              }`}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // --- Step 1: Defendants Represented ---
  const renderStep1 = () => (
    <div className="flex flex-col gap-y-8 w-[575px] mx-auto">
      <div className="bg-white py-6 px-10 rounded shadow-lg flex flex-col gap-y-6">
        <h2 className="text-xl font-bold text-[#1A1A1A]">Details of Party</h2>
        <h3 className="font-bold text-[#1A1A1A]">Plaintiff</h3>

        <div className="flex flex-col gap-y-4">
          <input
            type="text"
            placeholder="Full name"
            value={counsel.fullName}
            onChange={(e) => updateCounsel("fullName", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="email"
            placeholder="Email address"
            value={counsel.email}
            onChange={(e) => updateCounsel("email", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Address"
            value={counsel.address}
            onChange={(e) => updateCounsel("address", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Phone number"
            value={counsel.phone}
            onChange={(e) => updateCounsel("phone", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Whatsapp number"
            value={counsel.whatsapp}
            onChange={(e) => updateCounsel("whatsapp", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />
        </div>
      </div>

      <button
        onClick={nextStep}
        className="flex-1 bg-[#00B074] text-white py-4 rounded-xl font-medium text-lg hover:bg-[#009b65] transition-colors"
      >
        Next
      </button>
    </div>
  );

  // --- Step 2: Details of Counsel ---
  const renderStep2 = () => (
    <div className="flex flex-col gap-y-8 w-[575px] mx-auto">
      <div className="bg-white py-6 px-10 rounded shadow-lg flex flex-col gap-y-6">
        <h2 className="text-xl font-bold text-[#1A1A1A]">Details of Counsel</h2>
        <h3 className="font-bold text-[#1A1A1A]">Plaintiff Counsel</h3>

        <div className="flex flex-col gap-y-4">
          <input
            type="text"
            placeholder="Full name"
            value={counsel.fullName}
            onChange={(e) => updateCounsel("fullName", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="email"
            placeholder="Email address"
            value={counsel.email}
            onChange={(e) => updateCounsel("email", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Address"
            value={counsel.address}
            onChange={(e) => updateCounsel("address", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Phone number"
            value={counsel.phone}
            onChange={(e) => updateCounsel("phone", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Whatsapp number"
            value={counsel.whatsapp}
            onChange={(e) => updateCounsel("whatsapp", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />

          <input
            type="text"
            placeholder="Name of firm"
            value={counsel.firmName}
            onChange={(e) => updateCounsel("firmName", e.target.value)}
            className="w-full px-4 py-4 border border-[#E9EAEB] rounded-lg focus:outline-none focus:border-[#00B074] text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]"
          />
        </div>
      </div>

      <div className="flex gap-x-4">
        <button
          onClick={prevStep}
          className="flex-1 border border-[#00B074] text-[#00B074] py-4 rounded-xl font-medium text-lg hover:bg-green-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={nextStep}
          className="flex-1 bg-[#00B074] text-white py-4 rounded-xl font-medium text-lg hover:bg-[#009b65] transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );

  // --- Step 3: Upload Documents ---
  const renderStep3 = () => (
    <div className="flex flex-col gap-y-8 p-8 w-[575px] mx-auto shadow-lg">
      <h2 className="text-xl font-bold text-[#1A1A1A]">
        Upload case documents
      </h2>

      <div className="bg-white flex flex-col items-center justify-center gap-y-4">
        <h3 className="self-start font-bold text-[#1A1A1A] mb-2">
          Upload documents
        </h3>

        {documents.map((doc, idx) => (
          <div key={doc.id} className="w-full relative">
            <input
              type="file"
              id={`file-upload-${doc.id}`}
              accept="application/pdf"
              onChange={(e) => handleFileUpload(e, doc.id)}
              className="hidden"
            />
            {doc.file ? (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-[#E9EAEB]">
                <div className="flex items-center gap-x-3">
                  <FileText className="w-6 h-6 text-[#00B074]" />
                  <span className="text-sm font-medium text-[#1A1A1A] truncate max-w-[200px]">
                    {doc.file.name}
                  </span>
                </div>
                <div className="flex items-center gap-x-3">
                  <button
                    onClick={() => {
                      if (doc.file) {
                        const url = URL.createObjectURL(doc.file);
                        window.open(url, "_blank");
                      }
                    }}
                    className="text-sm font-medium text-[#00B074] hover:underline"
                  >
                    View
                  </button>
                  <label
                    htmlFor={`file-upload-${doc.id}`}
                    className="text-sm font-medium text-[#1A1A1A] cursor-pointer hover:underline"
                  >
                    Change
                  </label>
                  <button
                    onClick={() => removeDocumentSlot(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="w-full border-2 border-dashed border-[#E9EAEB] rounded-xl p-8 flex flex-col items-center justify-center gap-y-2 bg-white hover:bg-gray-50 transition-colors cursor-pointer relative">
                <label
                  htmlFor={`file-upload-${doc.id}`}
                  className="absolute inset-0 w-full h-full cursor-pointer"
                />
                <img
                  src={CLOUD_UPLOAD}
                  alt="cloud-upload"
                  className="w-10 h-10"
                />
                <div className="text-center">
                  <span className="text-[#00B074] font-bold">
                    Click to upload
                  </span>
                  <span className="text-[#475467]"> Example case.pdf</span>
                </div>
                <span className="text-[#475467] text-xs">
                  PDF only (max. 5 MB)
                </span>
                {documents.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      removeDocumentSlot(idx);
                    }}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700 z-10"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        <div className="self-end mt-2">
          <div
            onClick={addDocumentSlot}
            className="w-8 h-8 rounded-full bg-[#00B074] flex items-center justify-center text-white cursor-pointer hover:bg-[#009b65]"
          >
            <Plus className="w-5 h-5" />
          </div>
        </div>

        <div className="flex gap-x-4 w-full mt-8">
          <button
            onClick={prevStep}
            className="flex-1 border border-[#00B074] text-[#00B074] py-4 rounded-xl font-medium text-lg hover:bg-green-50 transition-colors"
          >
            Back
          </button>
          <button
            onClick={() => setSuccess(true)}
            className="flex-1 bg-[#00B074] text-white py-4 rounded-xl font-medium text-lg hover:bg-[#009b65] transition-colors"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto gap-y-7 py-6 items-center pb-20">
      <span className="text-[28px] font-bold">
        {currentStep === 1 && "Provides details of party"}

        {currentStep === 2 && "Provide details of counsel"}
        {currentStep === 3 && "Upload your case documents"}
      </span>

      <div className="w-full">
        {renderStepIndicator()}

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>

      <SuccessModal
        open={success}
        close={() => setSuccess(false)}
        message="Submitted Successfully!"
      />
    </div>
  );
};

export default FileAsThirdParty;
